import {
  createMoney,
  currencyMap,
  formatMoney,
  getCurrencySeparators,
  toMajorUnits,
  toMinorUnits,
} from '@poweredbypercent/utility-core'
import { useEffect, useRef, useState } from 'react'
import ReactCurrencyInputField from 'react-currency-input-field'
import styled from 'styled-components'
import { MoneyFieldProps } from './money-field.types'
import { theme as lemonadeTheme } from '../../../theme/theme'

const CurrencyInputBox = styled.div<
  Pick<MoneyFieldProps, 'status' | 'borderRadiiSize'>
>`
  ${({ theme, status = 'default', borderRadiiSize }) =>
    `
      display: inline-flex;
      position: relative;
      width: 100%;

      input {
        box-sizing: border-box;
        padding: ${theme.sizes.xs} ${theme.sizes.s};
        width: 100%;
        line-height: 24px;
        font-size: 14px;
        outline: none;
        color: ${theme.colors.black};

        &::placeholder {
          color: ${theme.colors.gray600};
        }

        border-radius: ${
          borderRadiiSize
            ? lemonadeTheme.borderRadii[borderRadiiSize]
            : '0.25rem'
        };

        ${
          status === 'danger'
            ? `
          border: 0.0625rem solid ${theme.colors.alert400};

          &:hover {
            outline: ${theme.sizes[1]} solid ${theme.colors.alert100};
          }
          &:focus, &:active {
            border-color: ${theme.colors.alert400};
          }
        `
            : `
          border: 0.0625rem solid ${theme.colors.gray80};

          &:hover {
            border-color: ${theme.colors.black};
            outline: ${theme.sizes[1]} solid ${theme.colors.gray80};
          }
          &:focus, &:active {
            border-color: ${theme.colors.black};;
          }
        `
        }

        &:disabled {
          background-color: ${theme.colors.gray80};

          &:hover {
            outline: none;
            border: 0.0625rem solid ${theme.colors.gray80};
          }
        }

       }
    `}
`

export const MoneyField = ({
  status,
  currency,
  locale,
  value,
  onValueChange,
  placeholder,
  borderRadiiSize,
  syncCurrencyToParent = true,
  ...InputFieldProps
}: MoneyFieldProps) => {
  const [actualValue, setActualValue] = useState<string | number | undefined>(
    undefined
  )
  const onValueChangeRef = useRef(onValueChange)
  onValueChangeRef.current = onValueChange

  useEffect(() => {
    setActualValue(value && toMajorUnits(value.amount, value.currency))
  }, [value])

  useEffect(() => {
    if (!syncCurrencyToParent || !value) return
    if (value.currency === currency) return
    onValueChangeRef.current?.(
      createMoney(toMajorUnits(value.amount, value.currency), currency),
      undefined
    )
  }, [syncCurrencyToParent, currency, value])

  const effectiveLocale =
    currency === 'USD' && locale === 'en-GB' ? 'en-US' : locale
  const minorUnits = currencyMap[currency]?.minorUnits ?? 2
  const allowsDecimals = minorUnits > 0
  const { groupSeparator, decimalSeparator } =
    getCurrencySeparators(effectiveLocale)

  return (
    <CurrencyInputBox status={status} borderRadiiSize={borderRadiiSize}>
      <ReactCurrencyInputField
        value={actualValue}
        allowDecimals={allowsDecimals}
        groupSeparator={groupSeparator}
        decimalSeparator={allowsDecimals ? decimalSeparator : undefined}
        placeholder={
          placeholder
            ? formatMoney({ amount: placeholder.amount, currency }, { locale })
            : undefined
        }
        onValueChange={(raw, __, parsed) => {
          if (
            parsed?.float !== null &&
            parsed?.float !== undefined &&
            currencyMap[currency]?.minorUnits > 0
          ) {
            const updated = createMoney(parsed?.float, currency)
            if (Math.abs(updated.amount) > Number.MAX_SAFE_INTEGER) return
            setActualValue(raw)
            if (updated.amount !== value?.amount) onValueChange?.(updated, raw)
            return
          }

          const asFloat = parseFloat(raw ?? '')

          if (isNaN(asFloat)) {
            setActualValue(raw)
            onValueChange?.(undefined, raw)
          } else {
            const asMinor = toMinorUnits(asFloat, currency)

            if (Math.abs(asMinor) > Number.MAX_SAFE_INTEGER) return
            setActualValue(raw)
            onValueChange?.(createMoney(asFloat, currency), raw)
          }
        }}
        disableAbbreviations
        intlConfig={{
          locale: effectiveLocale,
          currency,
        }}
        {...InputFieldProps}
      />
    </CurrencyInputBox>
  )
}
